import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactSortable } from 'react-sortablejs';
import SidebarLayout from 'components/Layout/Layout';
import { API_URL } from 'env';
import { Modal, Select, Collapse, Card, Row, Col, Input, Button, Switch } from 'antd';
import TinyMce from 'components/GbCpn/TinyMce';
import ImageUpload from 'components/GbCpn/ImageUpload';
import { FileAddOutlined, DeleteOutlined, EditOutlined, DragOutlined, SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import './AddPoint.scss';

interface DescriptionItem {
  id: string;
  title: string;
  content: string;
  image: string;
  position: 'Left' | 'Right' | 'Bottom' | 'Top';
}

interface Product {
  id: number;
  title: string;
  subtitle: string;
  point_price: number;
  locale: string;
  image: string;
  description: string;
}

const EditProduct: React.FC = () => {
  const navigate = useNavigate();
  const [bonusIds, setBonusIds] = useState<number[]>([]);

  const [form, setForm] = useState({
    name: '',
    description: '',
    platform: '',
    bot_file: '',
    settings: '',
    is_public: true,
    version: null
  } as any);

  const [descriptions, setDescriptions] = useState([] as any);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const { id } = useParams<{ id: string }>();

  const handleSave = async () => {
    if (!form.name.trim()) {
      Modal.warning({ title: 'Thiếu thông tin', content: 'Vui lòng nhập tên' });
      return;
    }
    if (!form.platform.trim()) {
      Modal.warning({ title: 'Thiếu thông tin', content: 'Vui lòng nhập platform' });
      return;
    }
    if (!form.bot_file) {
      Modal.warning({ title: 'Thiếu thông tin', content: 'Vui lòng nhập File Bot' });
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("platform", form.platform);
      formData.append("bot_file", form.bot_file);
      formData.append("is_public", form.is_public);

      if (form.description.trim()) formData.append("description", form.description);
      if (form.settings) formData.append("settings", form.settings);
      if (form.version.trim()) formData.append("version", form.version);

      const response = await fetch(`${API_URL}api/v1/be/bots/${id}/update`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: formData,
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const json = await response.json();

      if (json.status) {
        Modal.success({
          title: 'Success',
          content: 'Operation completed successfully',
          onOk: () => navigate('/bots'),
        });
      } else {
        Modal.error({
          title: 'Error',
          content: json.error[Object.keys(json.error)[0]] || 'Unknown error',
        });
      }
    } catch (error: any) {
      Modal.error({
        title: 'Error',
        content: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingData(true);
        const response = await fetch(`${API_URL}api/v1/be/bots/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const json = await response.json();
        if (json.status) {
          setForm({
            name: json.data.name ? json.data.name : '',
            description: json.data.description ? json.data.description : '',
            platform: json.data.platform ? json.data.platform : '',
            bot_file: json.data.file_url ? json.data.file_url : '',
            settings: json.data.settings ? json.data.settings : '',
            is_public: json.data.is_public ? json.data.is_public : true,
            version: json.data.version ? json.data.version : ''
          });

          if (json.data.introduction) {
            try {
              let parsedIntroduction = typeof json.data.introduction === 'string'
                ? JSON.parse(json.data.introduction)
                : json.data.introduction;
            } catch (error) {
              console.error('Error parsing introduction:', error);
            }
          }

          if (json.data.description) {
            try {
              let parsedDescriptions = typeof json.data.description === 'string'
                ? JSON.parse(json.data.description)
                : json.data.description;

              if (Array.isArray(parsedDescriptions)) {
                const descriptionsWithId = parsedDescriptions.map((desc: any, index: number) => ({
                  id: (index + 1).toString(),
                  code: desc.code || '',
                  points: desc.points || null,
                  max_times_per_user: desc.max_times_per_user || null,
                  cooldown_sec: desc.cooldown_sec || null,
                  expires_in_days: desc.expires_in_days || null
                }));
                setDescriptions(descriptionsWithId);
              }
            } catch (error) {
              console.error('Error parsing description:', error);
              setDescriptions([{
                id: '1',
                code: '',
                points: null,
                max_times_per_user: null,
                cooldown_sec: null,
                expires_in_days: null
              }]);
            }
          } else {
            setDescriptions([{
              id: '1',
              code: '',
              points: null,
              max_times_per_user: null,
              cooldown_sec: null,
              expires_in_days: null
            }]);
          }
        } else {
          Modal.error({
            title: "Lỗi",
            content: "Lấy dữ liệu thất bại",
            onOk: () => navigate("/point"),
          });
        }
      } catch (error: any) {
        Modal.error({
          title: "Lỗi",
          content: "Lỗi khi tải dữ liệu: " + error.message,
          onOk: () => navigate("/point"),
        });
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, [id, navigate]);
  return (
    <SidebarLayout title={<h4 className="text-2xl font-bold !m-0 py-4"></h4>}>
      <div className='head-form-box mb-4'>
        <div className='box-left'>
          <ArrowLeftOutlined
            onClick={() => navigate('/bots')}
          />
          <div className='title'>Add Bots</div>
        </div>
        <Button loading={loading} onClick={handleSave} type="primary" icon={<SaveOutlined />} size="large">
          Add new
        </Button>
      </div>
      <Row gutter={[16, 16]} className='AddProduct'>
        <Col span={24} lg={18}>
          <Card>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Name
                  </label>
                  <Input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Description
                  </label>
                  <Input
                    type="text"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Platform
                  </label>
                  <Input
                    type="text"
                    value={form.platform}
                    onChange={(e) => setForm({ ...form, platform: e.target.value })}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Version
                  </label>
                  <Input
                    type="number"
                    value={form.version}
                    onChange={(e) => setForm({ ...form, version: e.target.value })}
                  />
                </div>
              </Col>
              <Col span={24}>
                <div>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Settings
                  </label>
                  <Input
                    type="text"
                    value={form.settings}
                    onChange={(e) => setForm({ ...form, settings: e.target.value })}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <div>
                  <label className="block font-semibold mb-1" style={{ color: "#5e5873" }}>
                    File Bot
                  </label>
                  <Input
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setForm({ ...form, bot_file: file });
                      }
                    }}
                  />
                </div>
              </Col>

            </Row>
          </Card>
        </Col>
        <Col span={24} lg={6}>
          <Card>
            {/* <div className="mb-4">
              <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                Locale
              </label>
              <Select
                onChange={(e) => setForm({ ...form, locale: e })}
                value={form.locale}
                style={{ width: '100%' }}
              >
                <option value="vi">Vietnam</option>
                <option value="en">English</option>
              </Select>
            </div> */}
            <div>
              <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                Public
              </label>
              <Switch checked={form.is_public} onChange={(value) => setForm({ ...form, is_public: value })} />
            </div>
          </Card>
        </Col>
      </Row>
      <div className='ctrbox-foot mt-4'>
        <Button loading={loading} onClick={handleSave} type="primary" icon={<SaveOutlined />} size="large">
          Add new
        </Button>
      </div>
    </SidebarLayout>
  );
};

export default EditProduct;